import { format } from "date-fns";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import FilterTextField from "components/FilterTextField";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";

import { useEffect, useMemo, useState } from "react";
import Loading from "components/Loading";
import api from "services/api";
import { useFormik } from "formik";
import { useTranslate } from "utils/i18n";
import PropTypes from "prop-types";

import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";
import { buildRegionalChart } from "layouts/dashboard/dashboardChartHelpers";

function horizontalBarHeightForRows(n) {
  return `${Math.min(520, 152 + Math.max(Number(n) || 1, 1) * 38)}px`;
}

function ReportSection({ pdfId, children, sx }) {
  return (
    <Paper
      id={pdfId}
      elevation={0}
      sx={{
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: `1px solid ${figmaTokens.borderSubtle}`,
        bgcolor: figmaTokens.surface,
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

ReportSection.propTypes = {
  pdfId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

ReportSection.defaultProps = {
  sx: {},
};

function ReportTable({ rows, sectionLabel }) {
  const { t } = useTranslate();

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: figmaTokens.borderSubtle,
        overflow: "hidden",
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          ...directoryTableHeadStickyOverridesSx,
          ...directoryTableBodyCellDefaultsSx,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={directoryTableHeaderCellSx}>{sectionLabel}</TableCell>
            <TableCell align="right" sx={directoryTableHeaderCellSx}>
              {t("statistics.table.reported", "Reported")}
            </TableCell>
            <TableCell align="right" sx={directoryTableHeaderCellSx}>
              {t("statistics.table.reported_radius", "Within radius")}
            </TableCell>
            <TableCell align="right" sx={directoryTableHeaderCellSx}>
              {t("statistics.table.total", "Total")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow
              hover
              key={row.id ?? `${row.name}-${idx}`}
              sx={{
                "&:nth-of-type(even)": { bgcolor: "rgba(247, 249, 252, 0.85)" },
                "&:last-child td": { borderBottom: "none" },
              }}
            >
              <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
              <TableCell align="right">{row.teachersReported}</TableCell>
              <TableCell align="right">{row.teachersWithinRadius}</TableCell>
              <TableCell align="right">{row.totalTeachers}</TableCell>
            </TableRow>
          ))}
          <TableRow
            sx={{
              bgcolor: figmaTokens.tableRowHover,
              "& td": { fontWeight: 700, borderTop: `2px solid ${figmaTokens.borderSubtle}` },
            }}
          >
            <TableCell>{t("statistics.table.total_row", "Total")}</TableCell>
            <TableCell align="right">{rows.reduce((sum, row) => sum + Number(row.teachersReported ?? 0), 0)}</TableCell>
            <TableCell align="right">{rows.reduce((sum, row) => sum + Number(row.teachersWithinRadius ?? 0), 0)}</TableCell>
            <TableCell align="right">{rows.reduce((sum, row) => sum + Number(row.totalTeachers ?? 0), 0)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

ReportTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  sectionLabel: PropTypes.string.isRequired,
};

function Statistics() {
  const [loading, setLoading] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [province, setProvince] = useState([]);
  const [department, setDepartment] = useState([]);
  const [establishment, setEstablishment] = useState([]);
  const { t } = useTranslate();

  const fetchTeachers = async (dateData, radiusMetersVal) => {
    setLoading(true);
    try {
      const formattedDate = dateData
        ? format(new Date(dateData), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");
      const rm = Number(radiusMetersVal);
      const radiusM = Math.max(0, Math.round(Number.isFinite(rm) ? rm : 500000));
      const path = `reports/statistical?AttendDate=${encodeURIComponent(formattedDate)}&Radius=${encodeURIComponent(String(radiusM))}`;
      const base = String(api.defaults.baseURL || "").replace(/\/+$/, "");
      console.log("[Statistics] GET", `${base}/${path}`);
      const res = await api.get(path, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tk446655440000")}`,
        },
      });
      console.log("[Statistics] response", res.status, res.data);

      const teachersData = res.data || [];
      setProvince(teachersData.filter((item) => item.categoryType === "Province"));
      setDepartment(teachersData.filter((item) => item.categoryType === "Department"));
      setEstablishment(teachersData.filter((item) => item.categoryType === "Establishment"));
    } catch (err) {
      console.error("[Statistics] API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      date: format(new Date(), "yyyy-MM-dd"),
      radiusMeters: "500000",
    },
    onSubmit: async (values) => {
      await fetchTeachers(values.date, values.radiusMeters);
    },
  });

  useEffect(() => {
    fetchTeachers(formik.initialValues.date, formik.initialValues.radiusMeters);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once with defaults
  }, []);

  const establishmentChart = useMemo(
    () => buildRegionalChart(establishment, "Establishment", t),
    [establishment, t]
  );
  const provinceChart = useMemo(() => buildRegionalChart(province, "Province", t), [province, t]);
  const departmentChart = useMemo(() => buildRegionalChart(department, "Department", t), [department, t]);

  const exportPDF = async () => {
    setPdfExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const doc = new jsPDF("p", "pt", "a4");
      const margin = 14;
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      doc.setFontSize(14);
      doc.setTextColor(33, 113, 255);
      doc.text(t("statistics.pdf.title", "Teachers — statistical report"), pageW / 2, 28, { align: "center" });
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`${new Date().toLocaleString()}`, margin, 42);

      let y = 56;

      const captureAndPlace = async (elementId, sectionTitle) => {
        const el = document.getElementById(elementId);
        if (!el) return;
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });
        const imgData = canvas.toDataURL("image/png");
        const imageDims = doc.getImageProperties(imgData);
        const maxW = pageW - margin * 2;
        let imgW = maxW;
        let imgH = (imageDims.height * imgW) / imageDims.width;
        const footerSpace = 36;
        const maxH = pageH - y - footerSpace;
        if (imgH > maxH) {
          imgH = maxH;
          imgW = (imageDims.width * imgH) / imageDims.height;
        }
        if (y + imgH + 28 > pageH - footerSpace) {
          doc.addPage();
          y = margin + 12;
        }
        doc.setFontSize(11);
        doc.setTextColor(55);
        doc.text(sectionTitle, margin, y);
        y += 14;
        doc.addImage(imgData, "PNG", margin, y, imgW, imgH);
        y += imgH + 22;
      };

      const est = t("statistics.section.establishment", "Établissement");
      const prov = t("statistics.section.province", "Province");
      const dept = t("statistics.section.department", "Département");
      const tbl = t("statistics.pdf.table_block", "Table");
      const ch = t("statistics.pdf.chart_block", "Chart");

      const sequence = [
        ["pdf-table-establishment", `${est} — ${tbl}`],
        ["pdf-table-province", `${prov} — ${tbl}`],
        ["pdf-table-department", `${dept} — ${tbl}`],
        ["pdf-chart-establishment", `${est} — ${ch}`],
        ["pdf-chart-province", `${prov} — ${ch}`],
        ["pdf-chart-department", `${dept} — ${ch}`],
      ];

      for (const [id, title] of sequence) {
        await captureAndPlace(id, title);
      }

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i += 1) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(`${t("statistics.pdf.page", "Page")} ${i} / ${totalPages}`, pageW - margin - 2, pageH - 18, {
          align: "right",
        });
      }

      doc.save("teachers-report.pdf");
    } catch (e) {
      console.error("[Statistics] PDF export failed", e);
    } finally {
      setPdfExporting(false);
    }
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const nameHeader = t("statistics.table.name", "Nom");
    const reportedHeader = t("statistics.table.reported", "Nombre d'enseignants déclarés");
    const radiusHeader = t("statistics.table.reported_radius", "dans un rayon spécifié");
    const totalHeader = t("statistics.table.total", "Nombre d'enseignants Total");
    const totalLabel = t("statistics.table.total_row", "Totale");

    const addSheet = (sheetName, data) => {
      const rows = data.map((row) => ({
        [nameHeader]: row.name,
        [reportedHeader]: row.teachersReported,
        [radiusHeader]: row.teachersWithinRadius,
        [totalHeader]: row.totalTeachers,
      }));
      rows.push({
        [nameHeader]: totalLabel,
        [reportedHeader]: data.reduce((sum, r) => sum + r.teachersReported, 0),
        [radiusHeader]: data.reduce((sum, r) => sum + r.teachersWithinRadius, 0),
        [totalHeader]: data.reduce((sum, r) => sum + r.totalTeachers, 0),
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    };

    addSheet(t("statistics.section.establishment", "Établissement"), establishment);
    addSheet(t("statistics.section.province", "Province"), province);
    addSheet(t("statistics.section.department", "Département"), department);

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "teachers-report.xlsx");
  };

  const chartCompareHint = t(
    "statistics.chart.bar_compare_hint",
    "Reported vs within radius — same scale for both series."
  );

  const estTitle = t("statistics.section.establishment", "Établissement");
  const provTitle = t("statistics.section.province", "Province");
  const deptTitle = t("statistics.section.department", "Département");

  return (
    <DashboardLayout>
      {loading && <Loading />}
      <DashboardNavbar />
      <SoftBox py={3} className="content-area-main">
        <Card
          variant="outlined"
          sx={{
            mb: 2.5,
            borderRadius: 2,
            borderColor: "divider",
            boxShadow: "none",
          }}
        >
          <SoftBox sx={{ p: { xs: 1.5, md: 1.75 } }}>
            <Grid container spacing={1} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <SoftTypography variant="subtitle1" fontWeight="bold">
                  {t("statistics.title", "Statistiques")}
                </SoftTypography>
                <SoftTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {t("statistics.header_hint_tables_first", "Tables list raw figures; charts below summarize the same data.")}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent={{ xs: "stretch", md: "flex-end" }}>
                  <Button
                    type="button"
                    variant="contained"
                    disableElevation
                    disabled={pdfExporting || loading}
                    onClick={exportPDF}
                    startIcon={
                      pdfExporting ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : null
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      px: 2.25,
                      py: 1,
                      borderRadius: 2,
                      color: "#fff",
                      background: figmaTokens.gradientCSS,
                      boxShadow: "0 6px 18px rgba(102, 126, 234, 0.35)",
                      "&:hover": {
                        filter: "brightness(1.06)",
                        boxShadow: "0 8px 22px rgba(102, 126, 234, 0.4)",
                      },
                      "&.Mui-disabled": {
                        background: figmaTokens.gradientCSS,
                        opacity: 0.55,
                        color: "#fff",
                      },
                    }}
                  >
                    {t("statistics.export_pdf", "Exporter au format PDF")}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={exportExcel}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      px: 2.25,
                      py: 1,
                      borderRadius: 2,
                      color: figmaTokens.textPrimary,
                      borderWidth: 2,
                      borderColor: figmaTokens.borderStrong,
                      bgcolor: figmaTokens.surface,
                      "&:hover": {
                        borderWidth: 2,
                        borderColor: figmaTokens.accent,
                        bgcolor: "#fff",
                      },
                    }}
                  >
                    {t("statistics.export_excel", "Exporter Excel")}
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} component="form" onSubmit={formik.handleSubmit} sx={{ pt: 0 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={4} md={4}>
                    <FilterTextField
                      type="date"
                      name="date"
                      label={t("statistics.date_label", "Select date")}
                      value={formik.values.date}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <FilterTextField
                      type="number"
                      name="radiusMeters"
                      label={t("statistics.distance_meters", "Distance (meters)")}
                      value={formik.values.radiusMeters}
                      onChange={formik.handleChange}
                      inputProps={{ min: 0, step: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <SoftButton type="submit" variant="gradient" color="info" fullWidth disabled={loading} sx={{ minHeight: 48, py: 1.25 }}>
                      {t("statistics.search", "Recherche")}
                    </SoftButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>

        <ReportSection pdfId="pdf-table-establishment">
          <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {estTitle}
          </SoftTypography>
          <ReportTable rows={establishment} sectionLabel={estTitle} />
        </ReportSection>

        <ReportSection pdfId="pdf-table-province">
          <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {provTitle}
          </SoftTypography>
          <ReportTable rows={province} sectionLabel={provTitle} />
        </ReportSection>

        <ReportSection pdfId="pdf-table-department">
          <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {deptTitle}
          </SoftTypography>
          <ReportTable rows={department} sectionLabel={deptTitle} />
        </ReportSection>

        <SoftTypography variant="h6" fontWeight="bold" sx={{ mt: 1, mb: 2 }}>
          {t("statistics.visual_overview", "Charts")}
        </SoftTypography>
        <SoftTypography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t(
            "statistics.chart.section_intro",
            "Horizontal bars compare reported counts with within-radius counts using the same API fields as the tables above."
          )}
        </SoftTypography>

        <ReportSection pdfId="pdf-chart-establishment">
          <HorizontalBarChart
            variant="report"
            title={estTitle}
            description={chartCompareHint}
            height={horizontalBarHeightForRows(establishment.length)}
            chart={establishmentChart}
            showLegend
          />
        </ReportSection>

        <ReportSection pdfId="pdf-chart-province">
          <HorizontalBarChart
            variant="report"
            title={provTitle}
            description={chartCompareHint}
            height={horizontalBarHeightForRows(province.length)}
            chart={provinceChart}
            showLegend
          />
        </ReportSection>

        <ReportSection pdfId="pdf-chart-department">
          <HorizontalBarChart
            variant="report"
            title={deptTitle}
            description={chartCompareHint}
            height={horizontalBarHeightForRows(department.length)}
            chart={departmentChart}
            showLegend
          />
        </ReportSection>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Statistics;
